// src/utilies/turningArcTools/Add.js
import * as Cesium from "cesium";
import Type from "../Types";
import { getUsernameAndUserIdFormat, showToast } from "../Utility";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

const timeNow = () => Cesium.JulianDate.now();

const ENDPOINT_CACHE_REBUILD_MS = 500;

const MIN_RATIO = 0.2;
const MAX_RATIO = 1.5;
const RATIO_STEP = 0.05;
const REPEAT_MS = 200;
const REDRAW_DEBOUNCE_MS = 1000;

const MIN_SAMPLES = 10;
const MAX_SEGMENT_M = 1.0;

const HANDLE_RATIO = 0.35;
const HANDLE_MIN_M = 1.0;

const LAYER_CURBSTONE = "curbstone";
const LAYER_ROADEDGE = "roadedge";
const LAYER_GUARDRAIL = "guardrail";
const LAYER_CONCRETE_BARRIER = "concretebarrier";

const KEY_CURBSTONE = "curbstone";
const KEY_ROADEDGE = "roadedge";
const KEY_GUARDRAIL = "guardrail";
const KEY_CONCRETE_BARRIER = "concretebarrier";

const LAYER_LANE_MARKER = "lanemarker";
const LAYER_VIRTUAL_LINE = "virtualline";
const LAYER_TURNING_LINE = "turningline";
const LAYER_TURNING_ARC = "turningarc";

const KEY_LANE_MARKER = "lanemarker";
const KEY_VIRTUAL_LINE = "virtualline";
const KEY_TURNING_LINE = "turningline";
const KEY_TURNING_ARC = "turningarc";

const GROUP_A_KEYS = [KEY_CURBSTONE, KEY_ROADEDGE, KEY_GUARDRAIL, KEY_CONCRETE_BARRIER];
const GROUP_B_KEYS = [KEY_LANE_MARKER, KEY_VIRTUAL_LINE, KEY_TURNING_LINE, KEY_TURNING_ARC];

const normKey = (s) => (typeof s === "string" ? s.trim().toLowerCase() : "");

const getGroupIdByKey = (k) => {
  const kk = normKey(k);
  if (GROUP_A_KEYS.includes(kk)) return "A";
  if (GROUP_B_KEYS.includes(kk)) return "B";
  return "";
};

const getLayerKeyFromEntity = (entity, timeNowVal) => {
  try {
    const p = entity?.properties;
    const v = p?.layerName?.getValue?.(timeNowVal) ?? p?.layer_name?.getValue?.(timeNowVal) ?? p?.layer?.getValue?.(timeNowVal) ?? entity?.layerName ?? entity?.layer_name ?? entity?.layer;

    const k = normKey(v);
    if (k) return k;
  } catch {}
  return "";
};

const getOwnerDataSourceFromEntity = (entity) => {
  try {
    const ec = entity?.entityCollection;
    const owner = ec?.owner;
    return owner || null;
  } catch {
    return null;
  }
};

const resolveTargetFromPick = (picked, timeNowVal, dsCurbstone, dsRoadedge, dsGuardrail, dsConcreteBarrier, dsLaneMarker, dsVirtualLine, dsTurningLine, dsTurningArc) => {
  const entity = picked?.id || picked?.primitive?.id || picked || null;

  const byKey = getLayerKeyFromEntity(entity, timeNowVal);
  if (byKey === KEY_CURBSTONE) return { key: KEY_CURBSTONE, ds: dsCurbstone || null };
  if (byKey === KEY_ROADEDGE) return { key: KEY_ROADEDGE, ds: dsRoadedge || null };
  if (byKey === KEY_GUARDRAIL) return { key: KEY_GUARDRAIL, ds: dsGuardrail || null };
  if (byKey === KEY_CONCRETE_BARRIER) return { key: KEY_CONCRETE_BARRIER, ds: dsConcreteBarrier || null };

  if (byKey === KEY_LANE_MARKER) return { key: KEY_LANE_MARKER, ds: dsLaneMarker || null };
  if (byKey === KEY_VIRTUAL_LINE) return { key: KEY_VIRTUAL_LINE, ds: dsVirtualLine || null };
  if (byKey === KEY_TURNING_LINE) return { key: KEY_TURNING_LINE, ds: dsTurningLine || null };
  if (byKey === KEY_TURNING_ARC) return { key: KEY_TURNING_ARC, ds: dsTurningArc || null };

  const owner = getOwnerDataSourceFromEntity(entity);
  if (owner && owner === dsCurbstone) return { key: KEY_CURBSTONE, ds: dsCurbstone || null };
  if (owner && owner === dsRoadedge) return { key: KEY_ROADEDGE, ds: dsRoadedge || null };
  if (owner && owner === dsGuardrail) return { key: KEY_GUARDRAIL, ds: dsGuardrail || null };
  if (owner && owner === dsConcreteBarrier) return { key: KEY_CONCRETE_BARRIER, ds: dsConcreteBarrier || null };

  if (owner && owner === dsLaneMarker) return { key: KEY_LANE_MARKER, ds: dsLaneMarker || null };
  if (owner && owner === dsVirtualLine) return { key: KEY_VIRTUAL_LINE, ds: dsVirtualLine || null };
  if (owner && owner === dsTurningLine) return { key: KEY_TURNING_LINE, ds: dsTurningLine || null };
  if (owner && owner === dsTurningArc) return { key: KEY_TURNING_ARC, ds: dsTurningArc || null };

  return { key: "", ds: null };
};

const safeNowMs = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());

const getNumericPropValue = (entity, propName) => {
  const pbag = entity?.properties;
  if (!pbag) return null;

  const prop = pbag[propName];
  if (!prop) return null;

  try {
    const v = typeof prop.getValue === "function" ? prop.getValue(timeNow()) : prop;
    const n = typeof v === "number" ? v : Number(String(v).trim());
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const collectIdsFromGeoJSON = (gjson, out = new Set()) => {
  if (!gjson?.features || !Array.isArray(gjson.features)) return out;

  for (const f of gjson.features) {
    const n = Number(f?.properties?.id);
    if (Number.isFinite(n)) out.add(n);
  }

  return out;
};

const collectIdsFromDataSource = (ds, out = new Set()) => {
  const ents = ds?.entities?.values || [];

  for (const ent of ents) {
    const n = getNumericPropValue(ent, "id");
    if (Number.isFinite(n)) out.add(n);
  }

  return out;
};

const normalizeLayerIdentity = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[\s\-_]/g, "");

const findMapObjectByDataSource = (ds, stLocal) => {
  const list = Array.isArray(stLocal?.mapObjectsRef?.current) ? stLocal.mapObjectsRef.current : [];

  return (
    list.find((o) => {
      if (!o || !ds) return false;
      if (o.dataSource === ds || o.laneletDataSource === ds) return true;
      if (ds.layerId != null && o.layer_id != null && String(o.layer_id) === String(ds.layerId)) return true;

      const a = normalizeLayerIdentity(o.file_name || o.layer_name || o.name);
      const b = normalizeLayerIdentity(ds.layerName || ds.name);
      return a && b && a === b;
    }) || null
  );
};

const getNextIdPlus2 = (ds, stLocal = null) => {
  const layer = findMapObjectByDataSource(ds, stLocal);
  const ids = new Set();

  for (const gjson of [layer?.geojson, layer?.fullGeojson, layer?.hiddenGeojson, layer?.original_geojson, layer?.lenelet_geojson]) {
    collectIdsFromGeoJSON(gjson, ids);
  }

  collectIdsFromDataSource(ds, ids);

  const stored = Number(layer?.bboxMaxFeaturePropertiesId ?? layer?.bboxMaxId);
  const lastId = Number(layer?.geojson_last_id);

  let maxId = 0;
  if (Number.isFinite(stored)) maxId = Math.max(maxId, stored);
  if (Number.isFinite(lastId)) maxId = Math.max(maxId, lastId);

  for (const id of ids) {
    if (id > maxId) maxId = id;
  }

  let next = maxId + 2;
  while (ids.has(next)) {
    next += 2;
  }

  if (layer) {
    layer.geojson_last_id = next;
    layer.bboxMaxId = next;
    layer.bboxMaxFeaturePropertiesId = next;
  }

  const patch = (o) => {
    if (!o || !ds) return o;

    const sameLayer =
      o === layer ||
      o.dataSource === ds ||
      o.laneletDataSource === ds ||
      (ds.layerId != null && o.layer_id != null && String(o.layer_id) === String(ds.layerId)) ||
      (normalizeLayerIdentity(o.file_name || o.layer_name || o.name) && normalizeLayerIdentity(o.file_name || o.layer_name || o.name) === normalizeLayerIdentity(ds.layerName || ds.name));

    if (!sameLayer) return o;

    return {
      ...o,
      geojson_last_id: next,
      bboxMaxId: next,
      bboxMaxFeaturePropertiesId: next,
    };
  };

  if (Array.isArray(stLocal?.mapObjectsRef?.current)) {
    stLocal.mapObjectsRef.current = stLocal.mapObjectsRef.current.map(patch);
  }

  if (typeof stLocal?.setMapObjects === "function") {
    stLocal.setMapObjects((prev) => (Array.isArray(prev) ? prev.map(patch) : prev));
  }

  return Number.isFinite(next) ? next : 2;
};

const getPolylinePositions = (entity) => {
  const pl = entity?.polyline;
  if (!pl?.positions) return null;

  const v = pl.positions;
  const t = timeNow();

  if (typeof v?.getValue === "function") return v.getValue(t);
  if (Array.isArray(v)) return v;
  return null;
};

const getEndpointTangent = (positions, whichEnd) => {
  if (!positions || positions.length < 2) return null;

  if (whichEnd === "start") {
    const a = positions[0];
    const b = positions[1];
    if (!a || !b) return null;

    const v = Cesium.Cartesian3.subtract(b, a, new Cesium.Cartesian3());
    const mag = Cesium.Cartesian3.magnitude(v);
    if (!Number.isFinite(mag) || mag === 0) return null;

    return Cesium.Cartesian3.divideByScalar(v, mag, new Cesium.Cartesian3());
  }

  const a = positions[positions.length - 1];
  const b = positions[positions.length - 2];
  if (!a || !b) return null;

  // IMPORTANT: end tangent must be (end - prev), not (prev - end)
  const v = Cesium.Cartesian3.subtract(a, b, new Cesium.Cartesian3());
  const mag = Cesium.Cartesian3.magnitude(v);
  if (!Number.isFinite(mag) || mag === 0) return null;

  return Cesium.Cartesian3.divideByScalar(v, mag, new Cesium.Cartesian3());
};

const computeLengthMeters = (positions) => {
  if (!Array.isArray(positions) || positions.length < 2) return 0;
  let sum = 0;
  for (let i = 1; i < positions.length; i++) {
    const a = positions[i - 1];
    const b = positions[i];
    if (!a || !b) continue;
    sum += Cesium.Cartesian3.distance(a, b);
  }
  return sum;
};

const clonePropertiesFromEntityAtNow = (srcEntity) => {
  const src = srcEntity?.properties;
  if (!src) return null;

  const t = timeNow();
  const bag = new Cesium.PropertyBag({});

  const getNames = () => {
    try {
      if (typeof src.propertyNames === "function") return src.propertyNames();
      if (Array.isArray(src.propertyNames)) return src.propertyNames;
      if (Array.isArray(src._propertyNames)) return src._propertyNames;
      return Object.keys(src).filter((k) => k && !k.startsWith("_") && k !== "propertyNames");
    } catch {
      return [];
    }
  };

  const names = getNames();

  for (let i = 0; i < names.length; i++) {
    const name = names[i];
    if (!name || name === "id") continue;

    const p = src[name];
    if (!p) continue;

    try {
      const v = typeof p.getValue === "function" ? p.getValue(t) : p;
      bag.addProperty?.(name);
      bag[name] = new Cesium.ConstantProperty(v);
    } catch {}
  }

  return bag;
};

const pickPolylineStyleFromDataSource = (ds) => {
  const t = timeNow();
  const ents = ds?.entities?.values;
  if (!Array.isArray(ents) || ents.length === 0) return null;

  const first = ents.find((e) => e?.polyline);
  if (!first?.polyline) return null;

  const pl = first.polyline;
  const getVal = (p) => (typeof p?.getValue === "function" ? p.getValue(t) : p);

  return {
    width: getVal(pl.width),
    clampToGround: getVal(pl.clampToGround),
    material: pl.material,
    arcType: getVal(pl.arcType),
    zIndex: getVal(pl.zIndex),
  };
};

const projectToWindow = (viewer, cart) => {
  try {
    const p = viewer?.scene?.cartesianToCanvasCoordinates?.(cart);
    if (p) return p;
  } catch {}

  try {
    const p2 = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, cart);
    if (p2) return p2;
  } catch {}

  try {
    return Cesium.SceneTransforms.wgs84ToWindowCoordinates(viewer.scene, cart);
  } catch {
    return null;
  }
};

const clampRect = (a, b) => {
  const left = Math.min(a.x, b.x);
  const right = Math.max(a.x, b.x);
  const top = Math.min(a.y, b.y);
  const bottom = Math.max(a.y, b.y);
  return { left, right, top, bottom, width: right - left, height: bottom - top };
};

const insideRect = (p, r) => {
  if (!p || !r) return false;
  return p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom;
};

const getENUFrameAt = (origin) => Cesium.Transforms.eastNorthUpToFixedFrame(origin);

const toLocalENU2D = (origin, target) => {
  const inv = new Cesium.Matrix4();
  Cesium.Matrix4.inverse(getENUFrameAt(origin), inv);
  const p = Cesium.Matrix4.multiplyByPoint(inv, target, new Cesium.Cartesian3());
  return { x: p.x, y: p.y, z: p.z };
};

const toLocalENUVec2D = (origin, worldVec) => {
  const inv = new Cesium.Matrix4();
  Cesium.Matrix4.inverse(getENUFrameAt(origin), inv);
  const v = Cesium.Matrix4.multiplyByPointAsVector(inv, worldVec, new Cesium.Cartesian3());
  return { x: v.x, y: v.y, z: v.z };
};

const fromLocalENU = (origin, local) => {
  const m = getENUFrameAt(origin);
  const p = new Cesium.Cartesian3(local.x, local.y, local.z || 0);
  return Cesium.Matrix4.multiplyByPoint(m, p, new Cesium.Cartesian3());
};

const normalize2 = (v) => {
  const len = Math.hypot(v.x, v.y);
  if (!Number.isFinite(len) || len <= 1e-9) return { x: 1, y: 0 };
  return { x: v.x / len, y: v.y / len };
};

const rightNormal2 = (t) => ({ x: t.y, y: -t.x });
const leftNormal2 = (t) => ({ x: -t.y, y: t.x });

const cross2 = (a, b) => a.x * b.y - a.y * b.x;

const quadBezier2 = (p0, p1, p2, t) => {
  const u = 1 - t;
  const uu = u * u;
  const tt = t * t;
  return {
    x: uu * p0.x + 2 * u * t * p1.x + tt * p2.x,
    y: uu * p0.y + 2 * u * t * p1.y + tt * p2.y,
  };
};

const cubicBezier2 = (p0, c1, c2, p3, t) => {
  const u = 1 - t;
  const uu = u * u;
  const uuu = uu * u;
  const tt = t * t;
  const ttt = tt * t;

  return {
    x: uuu * p0.x + 3 * uu * t * c1.x + 3 * u * tt * c2.x + ttt * p3.x,
    y: uuu * p0.y + 3 * uu * t * c1.y + 3 * u * tt * c2.y + ttt * p3.y,
  };
};

const buildTurningArcPositions = (startWorld, endWorld, startTangentWorld, endTangentWorld, ratio) => {
  const ratioClamped = Math.max(MIN_RATIO, Math.min(MAX_RATIO, ratio));

  const endLocal = toLocalENU2D(startWorld, endWorld);
  const dist = Math.hypot(endLocal.x, endLocal.y);
  if (!Number.isFinite(dist) || dist < 0.01) return [startWorld, endWorld];

  const chord = normalize2({ x: endLocal.x, y: endLocal.y });

  let t0;
  if (startTangentWorld) {
    const v0 = toLocalENUVec2D(startWorld, startTangentWorld);
    t0 = normalize2({ x: v0.x, y: v0.y });
  } else {
    t0 = chord;
  }

  let t1;
  if (endTangentWorld) {
    const v1 = toLocalENUVec2D(startWorld, endTangentWorld);
    t1 = normalize2({ x: v1.x, y: v1.y });
  } else {
    t1 = chord;
  }

  // Keep tangents generally toward end (avoid going backward first)
  if (t0.x * chord.x + t0.y * chord.y < 0) t0 = { x: -t0.x, y: -t0.y };
  if (t1.x * chord.x + t1.y * chord.y < 0) t1 = { x: -t1.x, y: -t1.y };

  // No extra straight segments: only one smooth curve from P0 to P3
  const baseHandle = Math.max(HANDLE_MIN_M, dist * HANDLE_RATIO);
  const h = Math.min(dist * 0.9, baseHandle / ratioClamped);

  const P0 = { x: 0, y: 0 };
  const P3 = { x: endLocal.x, y: endLocal.y };

  const C1 = { x: P0.x + t0.x * h, y: P0.y + t0.y * h };
  const C2 = { x: P3.x - t1.x * h, y: P3.y - t1.y * h };

  const approx = dist + 2 * h;
  const needBySeg = Math.ceil(approx / MAX_SEGMENT_M);
  const samples = Math.max(MIN_SAMPLES, needBySeg);

  const out = [];
  for (let i = 0; i <= samples; i++) {
    const tt = i / samples;
    const p = cubicBezier2(P0, C1, C2, P3, tt);
    out.push(fromLocalENU(startWorld, { x: p.x, y: p.y, z: 0 }));
  }

  return out;
};

const addOrUpdateTurningArcEntity = (targetDs, stLocal, positions, targetLineEntity) => {
  if (!targetDs || !Array.isArray(positions) || positions.length < 2) return null;

  const len = computeLengthMeters(positions).toFixed(2);

  if (stLocal.arcEntity) {
    try {
      stLocal.arcEntity.polyline.positions = new Cesium.ConstantProperty(positions);

      const numericId = Number.isFinite(stLocal.arcNumericId) ? stLocal.arcNumericId : getNextIdPlus2(targetDs, stLocal);

      const nextProps = clonePropertiesFromEntityAtNow(targetLineEntity) || new Cesium.PropertyBag({});
      nextProps.addProperty?.("id");
      nextProps.id = new Cesium.ConstantProperty(numericId);

      const creator = getUsernameAndUserIdFormat(stLocal.userData);
      if (creator) {
        nextProps.addProperty?.("creator");
        nextProps.creator = new Cesium.ConstantProperty(creator);
      }

      stLocal.arcEntity.properties = nextProps;
      stLocal.arcNumericId = numericId;

      return stLocal.arcEntity;
    } catch {}
  }

  const numericId = getNextIdPlus2(targetDs, stLocal);
  const baseStyle = stLocal.turningLineStyle;

  const props = clonePropertiesFromEntityAtNow(targetLineEntity) || new Cesium.PropertyBag({});
  props.addProperty?.("id");
  props.id = new Cesium.ConstantProperty(numericId);

  const creator = getUsernameAndUserIdFormat(stLocal.userData);
  if (creator) {
    props.addProperty?.("creator");
    props.creator = new Cesium.ConstantProperty(creator);
  }

  const entId = `turning-arc-${numericId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const ent = targetDs.entities.add({
    id: entId,
    name: "turning_arc",
    properties: props,
    polyline: {
      positions,
      width: baseStyle?.width ?? 3,
      material: baseStyle?.material,
      clampToGround: baseStyle?.clampToGround ?? true,
      arcType: baseStyle?.arcType,
      zIndex: baseStyle?.zIndex,
    },
  });

  stLocal.arcEntity = ent;
  stLocal.arcNumericId = numericId;

  try {
    stLocal.undoStack?.push({ ds: targetDs, id: entId });
  } catch {}

  return ent;
};

// -----------------------
// Module state
// -----------------------
const makeInitialState = () => ({
  enabled: false,
  viewer: null,
  handler: null,
  onEdit: null,
  targetEntityForProps: null,
  originalCameraControls: null,
  camLocked: false,
  onDisable: null,
  userData: null,
  mapObjectsRef: null,
  setMapObjects: null,
  laneMarkerDs: null,
  virtualLineDs: null,
  turningLineDs: null,
  turningArcDs: null,

  startGroupId: "",

  curbstoneDs: null,
  roadedgeDs: null,
  guardrailDs: null,
  concreteBarrierDs: null,

  turningLineStyle: null,

  endpointCache: [],
  endpointCacheBuiltAt: 0,

  dragging: false,
  dragStart: null,
  dragEnd: null,
  overlayDiv: null,

  startPick: null,
  endPick: null,

  ratio: 1.0,
  arcEntity: null,
  arcNumericId: null,

  undoStack: [],

  canAdjust: false,

  repeatTid: null,
  redrawTid: null,
  rafId: null,
  pendingRedraw: false,

  finalizeTid: null,

  targetKey: "",
  targetDs: null,
  startMarker: null,
  endMarker: null,
});

let st = makeInitialState();

const destroyOverlay = () => {
  if (st.overlayDiv && st.overlayDiv.parentElement) {
    try {
      st.overlayDiv.parentElement.removeChild(st.overlayDiv);
    } catch {}
  }
  st.overlayDiv = null;
};
const removeSelectionMarkers = () => {
  try {
    const ents = st.viewer?.entities;
    if (st.startMarker && ents) ents.remove(st.startMarker);
    if (st.endMarker && ents) ents.remove(st.endMarker);
  } catch {}
  st.startMarker = null;
  st.endMarker = null;
};

const upsertSelectionMarker = (kind, world) => {
  const viewer = st.viewer;
  if (!viewer) return null;

  const ents = viewer.entities;
  const isStart = kind === "start";
  const existing = isStart ? st.startMarker : st.endMarker;

  if (!world) {
    try {
      if (existing && ents) ents.remove(existing);
    } catch {}
    if (isStart) st.startMarker = null;
    else st.endMarker = null;
    return null;
  }

  const id = isStart ? "turningarc-start-marker" : "turningarc-end-marker";
  const color = isStart ? Cesium.Color.LIME : Cesium.Color.CYAN;

  if (existing) {
    try {
      existing.position = new Cesium.ConstantPositionProperty(world);
      existing.show = true;
      return existing;
    } catch {}
  }

  const e = ents.add({
    id,
    name: id,
    position: world,
    point: {
      pixelSize: 10,
      color,
      outlineColor: Cesium.Color.BLACK.withAlpha(0.8),
      outlineWidth: 2,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
    },
  });

  if (isStart) st.startMarker = e;
  else st.endMarker = e;

  return e;
};

const ensureOverlay = () => {
  if (!st.viewer?.container) return;
  if (st.overlayDiv) return;

  const d = document.createElement("div");
  d.style.position = "absolute";
  d.style.pointerEvents = "none";
  d.style.border = "2px dashed rgba(255, 0, 200, 0.9)";
  d.style.background = "rgba(255, 0, 200, 0.08)";
  d.style.left = "0px";
  d.style.top = "0px";
  d.style.width = "0px";
  d.style.height = "0px";
  d.style.display = "none";

  st.viewer.container.style.position = st.viewer.container.style.position || "relative";
  st.viewer.container.appendChild(d);
  st.overlayDiv = d;
};

const drawOverlayRect = (r) => {
  if (!st.overlayDiv) return;

  if (!r || r.width < 2 || r.height < 2) {
    st.overlayDiv.style.display = "none";
    return;
  }

  st.overlayDiv.style.display = "block";
  st.overlayDiv.style.left = `${r.left}px`;
  st.overlayDiv.style.top = `${r.top}px`;
  st.overlayDiv.style.width = `${r.width}px`;
  st.overlayDiv.style.height = `${r.height}px`;
};

const stopKeyRepeat = () => {
  if (st.repeatTid) clearInterval(st.repeatTid);
  st.repeatTid = null;
};

const cancelRedrawDebounce = () => {
  cancelFinalizeDebounce();
  cancelRaf();
};

const cancelFinalizeDebounce = () => {
  if (st.finalizeTid) clearTimeout(st.finalizeTid);
  st.finalizeTid = null;
};

const cancelRaf = () => {
  if (st.rafId) cancelAnimationFrame(st.rafId);
  st.rafId = null;
  st.pendingRedraw = false;
};

const unlockCameraIfNeeded = () => {
  if (!st.viewer) return;
  if (!st.originalCameraControls) return;
  if (!st.camLocked) return;
  try {
    restoreCameraControls(st.viewer, st.originalCameraControls);
  } catch {}
  st.camLocked = false;
};

const lockCameraIfNeeded = () => {
  if (!st.viewer) return;
  if (!st.originalCameraControls) return;
  if (st.camLocked) return;
  try {
    const ok = disableCameraControls(st.viewer, st.originalCameraControls);
    st.camLocked = Boolean(ok);
  } catch {
    st.camLocked = false;
  }
};

const resetSelection = (keepArc = true) => {
  st.dragging = false;
  st.dragStart = null;
  st.dragEnd = null;
  drawOverlayRect(null);

  st.startPick = null;
  st.endPick = null;

  removeSelectionMarkers();
  if (!keepArc) {
    try {
      if (st.arcEntity && st.targetDs) st.targetDs.entities.remove(st.arcEntity);
    } catch {}
    st.arcEntity = null;
    st.arcNumericId = null;
  }

  st.canAdjust = false;

  st.targetKey = "";
  st.targetDs = null;
  st.startGroupId = "";
  st.targetEntityForProps = null;
};

const rebuildEndpointCache = () => {
  const viewer = st.viewer;
  if (!viewer) return;

  const next = [];

  const addFromDs = (ds, layerKey) => {
    const ents = ds?.entities?.values;
    if (!Array.isArray(ents) || ents.length === 0) return;

    for (let i = 0; i < ents.length; i++) {
      const e = ents[i];
      if (!e?.polyline) continue;

      const pos = getPolylinePositions(e);
      if (!Array.isArray(pos) || pos.length < 2) continue;

      const s = pos[0];
      const t = pos[pos.length - 1];
      const tanS = getEndpointTangent(pos, "start");
      const tanE = getEndpointTangent(pos, "end");

      if (s && tanS) next.push({ layerKey, entity: e, end: "start", world: s, tangentWorld: tanS });
      if (t && tanE) next.push({ layerKey, entity: e, end: "end", world: t, tangentWorld: tanE });
    }
  };

  if (st.curbstoneDs) addFromDs(st.curbstoneDs, LAYER_CURBSTONE);
  if (st.roadedgeDs) addFromDs(st.roadedgeDs, LAYER_ROADEDGE);
  if (st.guardrailDs) addFromDs(st.guardrailDs, LAYER_GUARDRAIL);
  if (st.concreteBarrierDs) addFromDs(st.concreteBarrierDs, LAYER_CONCRETE_BARRIER);

  if (st.laneMarkerDs) addFromDs(st.laneMarkerDs, LAYER_LANE_MARKER);
  if (st.virtualLineDs) addFromDs(st.virtualLineDs, LAYER_VIRTUAL_LINE);
  if (st.turningLineDs) addFromDs(st.turningLineDs, LAYER_TURNING_LINE);
  if (st.turningArcDs) addFromDs(st.turningArcDs, LAYER_TURNING_ARC);

  st.endpointCache = next;
  st.endpointCacheBuiltAt = safeNowMs();
};

const ensureEndpointCache = (force = false) => {
  const now = safeNowMs();
  if (force || now - (st.endpointCacheBuiltAt || 0) > ENDPOINT_CACHE_REBUILD_MS) rebuildEndpointCache();
};

const COINCIDENT_TOL_M = 0.005;

const pickSingleEndpointInRect = (rect) => {
  const viewer = st.viewer;
  if (!viewer) return null;

  ensureEndpointCache(false);

  const hits = [];
  for (let i = 0; i < st.endpointCache.length; i++) {
    const ep = st.endpointCache[i];
    const win = projectToWindow(viewer, ep.world);
    if (!win) continue;
    if (insideRect(win, rect)) hits.push(ep);
  }

  if (hits.length === 1) return { ok: true, count: 1, hit: hits[0] };
  if (hits.length === 0) return { ok: false, count: 0, hit: null };

  const base = hits[0]?.world;
  let same = !!base;
  for (let i = 1; i < hits.length && same; i++) {
    const d = Cesium.Cartesian3.distance(base, hits[i].world);
    if (!Number.isFinite(d) || d > COINCIDENT_TOL_M) same = false;
  }

  if (same) return { ok: true, count: 1, hit: hits[0] };
  return { ok: false, count: hits.length, hit: null };
};

const getTargetDsByLayerKey = (layerKey) => {
  const k = normKey(layerKey);
  if (k === KEY_CURBSTONE) return st.curbstoneDs;
  if (k === KEY_ROADEDGE) return st.roadedgeDs;
  if (k === KEY_GUARDRAIL) return st.guardrailDs;
  if (k === KEY_CONCRETE_BARRIER) return st.concreteBarrierDs;

  if (k === KEY_LANE_MARKER) return st.laneMarkerDs;
  if (k === KEY_VIRTUAL_LINE) return st.virtualLineDs;
  if (k === KEY_TURNING_LINE) return st.turningLineDs;
  if (k === KEY_TURNING_ARC) return st.turningArcDs;

  return null;
};

const redrawPreviewNow = () => {
  if (!st.enabled) return;
  if (!st.canAdjust) return;
  if (!st.arcEntity) return;
  if (!st.startPick || !st.endPick) return;
  if (!st.targetDs) return;

  try {
    const positions = buildTurningArcPositions(st.startPick.world, st.endPick.world, st.startPick.tangentWorld, st.endPick.tangentWorld, st.ratio);

    addOrUpdateTurningArcEntity(st.targetDs, st, positions, st.targetEntityForProps);
  } catch {}
};

const requestSmoothRedraw = () => {
  if (st.pendingRedraw) return;
  st.pendingRedraw = true;

  st.rafId = requestAnimationFrame(() => {
    st.rafId = null;
    st.pendingRedraw = false;
    redrawPreviewNow();
  });
};

const scheduleRedraw = () => {
  requestSmoothRedraw();

  cancelFinalizeDebounce();
  st.finalizeTid = setTimeout(() => {
    st.finalizeTid = null;

    if (!st.enabled) return;
    if (!st.canAdjust) return;
    if (!st.arcEntity) return;

    redrawPreviewNow();

    try {
      st.onEdit?.(st.targetKey || "");
    } catch {}
  }, REDRAW_DEBOUNCE_MS);
};

const applyArcNow = () => {
  if (!st.startPick || !st.endPick) return;
  if (!st.targetDs) {
    showToast("Failed to detect target layer for the end point.", Type.NOTIFICATION.ERROR, { duration: 5000 });
    return;
  }

  try {
    const positions = buildTurningArcPositions(st.startPick.world, st.endPick.world, st.startPick.tangentWorld, st.endPick.tangentWorld, st.ratio);

    addOrUpdateTurningArcEntity(st.targetDs, st, positions, st.targetEntityForProps);

    try {
      st.onEdit?.(st.targetKey || "");
    } catch {}

    st.canAdjust = true;

    showToast("Turning arc created. Use ↑/↓ to adjust ratio.", Type.NOTIFICATION.SUCCESS, { duration: 3500 });
  } catch {
    showToast("Failed to create turning arc.", Type.NOTIFICATION.ERROR, { duration: 5000 });
  }
};

const setRatio = (next) => {
  const v = Math.max(MIN_RATIO, Math.min(MAX_RATIO, next));
  if (Math.abs(v - st.ratio) < 1e-9) return;
  st.ratio = v;
  scheduleRedraw();
};

const startKeyRepeat = (dir) => {
  stopKeyRepeat();
  st.repeatTid = setInterval(() => {
    if (!st.enabled) return;
    if (!st.canAdjust) return;
    if (!st.arcEntity) return;
    setRatio(st.ratio + dir * RATIO_STEP);
  }, REPEAT_MS);
};

const moveArcTo = (destKey) => {
  if (!st.enabled) return;
  if (!st.canAdjust) return;
  if (!st.arcEntity) return;

  const dk = normKey(destKey);
  if (dk !== KEY_LANE_MARKER && dk !== KEY_VIRTUAL_LINE) return;

  if (normKey(st.targetKey) === dk) return;

  const destDs = dk === KEY_LANE_MARKER ? st.laneMarkerDs : st.virtualLineDs;
  if (!destDs) {
    showToast(`${dk} layer not found.`, Type.NOTIFICATION.ERROR, { duration: 3000 });
    return;
  }

  const positions = getPolylinePositions(st.arcEntity);
  if (!Array.isArray(positions) || positions.length < 2) return;

  const srcEnt = st.arcEntity;

  try {
    if (st.targetDs?.entities && srcEnt) st.targetDs.entities.remove(srcEnt);
  } catch {}

  st.arcEntity = null;
  st.arcNumericId = null;

  st.targetKey = dk;
  st.targetDs = destDs;

  try {
    st.turningLineStyle = pickPolylineStyleFromDataSource(destDs) || st.turningLineStyle;
  } catch {}

  try {
    addOrUpdateTurningArcEntity(destDs, st, positions, srcEnt);
    st.onEdit?.(st.targetKey || "");
  } catch {}
};

const onKeyDown = (e) => {
  if (!st.enabled) return;

  const tag = e.target?.tagName?.toLowerCase();
  if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;

  if (e.key === "Escape") {
    e.preventDefault();

    const hasSomethingToReset =
      Boolean(st.dragging) ||
      Boolean(st.startPick) ||
      Boolean(st.endPick) ||
      Boolean(st.canAdjust) ||
      Boolean(st.startMarker) ||
      Boolean(st.endMarker) ||
      Boolean(st.targetDs) ||
      Boolean(st.targetKey) ||
      Boolean(st.targetEntityForProps);

    if (hasSomethingToReset) {
      resetSelection(true);
      showToast("Selection cleared. Press ESC again to disable the tool.", Type.NOTIFICATION.INFO, { duration: 2500 });
      return;
    }

    try {
      st.onDisable?.();
    } catch {}

    showToast("Turning Arc tool disabled.", Type.NOTIFICATION.INFO, { duration: 2000 });
    cleanup();
    return;
  }

  if (!st.canAdjust) return;
  if (!st.arcEntity) return;

  if (e.key === "m" || e.key === "M") {
    e.preventDefault();
    moveArcTo(KEY_LANE_MARKER);
    return;
  }

  if (e.key === "v" || e.key === "V") {
    e.preventDefault();
    moveArcTo(KEY_VIRTUAL_LINE);
    return;
  }

  if (e.key === "ArrowUp") {
    e.preventDefault();
    setRatio(st.ratio + RATIO_STEP);
    startKeyRepeat(+1);
  } else if (e.key === "ArrowDown") {
    e.preventDefault();
    setRatio(st.ratio - RATIO_STEP);
    startKeyRepeat(-1);
  }
};

const onKeyUp = (e) => {
  if (e.key === "ArrowUp" || e.key === "ArrowDown") stopKeyRepeat();
};

const cleanup = () => {
  stopKeyRepeat();
  cancelRedrawDebounce();

  try {
    if (st.handler) st.handler.destroy();
  } catch {}
  st.handler = null;

  destroyOverlay();
  removeSelectionMarkers();
  unlockCameraIfNeeded();

  try {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
  } catch {}

  const prevUndoStack = st?.undoStack;
  st = makeInitialState();
  if (Array.isArray(prevUndoStack) && prevUndoStack.length) st.undoStack = prevUndoStack;
};

const setupHandler = () => {
  const viewer = st.viewer;
  if (!viewer) return;

  ensureOverlay();
  st.handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  const abandonPreviousAdjustIfAny = () => {
    if (!st.canAdjust || !st.arcEntity) return;

    try {
      redrawPreviewNow();
    } catch {}

    try {
      st.onEdit?.(st.targetKey || "");
    } catch {}

    stopKeyRepeat();
    cancelRedrawDebounce();

    st.canAdjust = false;
    st.arcEntity = null;
    st.arcNumericId = null;
    st.startPick = null;
    st.endPick = null;
    st.targetKey = "";
    st.targetDs = null;
    st.targetEntityForProps = null;
  };

  const beginDrag = (pos) => {
    abandonPreviousAdjustIfAny();
    st.dragging = true;
    st.dragStart = pos;
    st.dragEnd = pos;

    lockCameraIfNeeded();
    drawOverlayRect(clampRect(st.dragStart, st.dragEnd));
  };

  const updateDrag = (pos) => {
    if (!st.dragging) return;
    st.dragEnd = pos;
    drawOverlayRect(clampRect(st.dragStart, st.dragEnd));
  };

  const endDrag = () => {
    if (!st.dragging || !st.dragStart || !st.dragEnd) return;

    st.dragging = false;
    unlockCameraIfNeeded();

    const rect = clampRect(st.dragStart, st.dragEnd);
    drawOverlayRect(null);

    if (rect.width < 4 || rect.height < 4) {
      showToast("Draw a small box to pick exactly one endpoint.", Type.NOTIFICATION.INFO, { duration: 2500 });
      return;
    }

    const res = pickSingleEndpointInRect(rect);
    if (!res.ok) {
      showToast(`Invalid box: found ${res.count} endpoint(s). Need exactly 1.`, Type.NOTIFICATION.ERROR, { duration: 3500 });
      return;
    }

    const hit = res.hit;

    if (!st.startPick) {
      st.startPick = hit;
      st.startGroupId = getGroupIdByKey(hit?.layerKey || "");
      st.endPick = null;
      st.ratio = 1.0;
      upsertSelectionMarker("start", hit?.world);
      upsertSelectionMarker("end", null);

      showToast("Start selected. Draw end box.", Type.NOTIFICATION.SUCCESS, { duration: 2500 });
      return;
    }

    const startG = st.startGroupId || getGroupIdByKey(st.startPick?.layerKey || "");
    const endG = getGroupIdByKey(hit?.layerKey || "");
    if (!startG || !endG || startG !== endG) {
      st.endPick = null;
      upsertSelectionMarker("end", null);
      showToast("End must be from the same group as start.", Type.NOTIFICATION.ERROR, { duration: 3500 });
      return;
    }

    st.endPick = hit;
    upsertSelectionMarker("end", hit?.world);
    st.targetEntityForProps = null;

    try {
      const win = projectToWindow(viewer, hit?.world);
      if (win) {
        const picks = viewer.scene.drillPick(win, 10) || [];
        const pickedAny = picks[0];
        const pickedEnt =
          picks.find((p) => (p?.id || p?.primitive?.id)?.polyline)?.id || picks.find((p) => (p?.id || p?.primitive?.id)?.polyline)?.primitive?.id || pickedAny?.id || pickedAny?.primitive?.id || null;

        st.targetEntityForProps = pickedEnt;
      }
    } catch {}

    const startKey = normKey(st.startPick?.layerKey || "");
    const endKey = normKey(hit?.layerKey || "");
    const bothFromLaneOrVirtual = (startKey === KEY_LANE_MARKER || startKey === KEY_VIRTUAL_LINE) && (endKey === KEY_LANE_MARKER || endKey === KEY_VIRTUAL_LINE);

    const pickedKey = bothFromLaneOrVirtual ? KEY_VIRTUAL_LINE : endKey;

    st.targetKey = pickedKey;

    const dsByCacheKey = getTargetDsByLayerKey(pickedKey);
    st.targetDs = dsByCacheKey || null;

    if (bothFromLaneOrVirtual && !st.targetDs) {
      showToast("VirtualLine layer not found.", Type.NOTIFICATION.ERROR, { duration: 3500 });
      return;
    }

    if (!st.targetDs) {
      try {
        const picked = viewer.scene.pick(st.dragEnd || st.dragStart);
        const r = resolveTargetFromPick(picked, timeNow(), st.curbstoneDs, st.roadedgeDs, st.guardrailDs, st.concreteBarrierDs, st.laneMarkerDs, st.virtualLineDs, st.turningLineDs, st.turningArcDs);

        st.targetKey = normKey(r?.key || pickedKey);
        st.targetDs = r?.ds || null;
      } catch {}
    }

    if (!st.targetDs) {
      showToast("End selected, but target layer not detected.", Type.NOTIFICATION.ERROR, { duration: 3500 });
      return;
    }

    st.turningLineStyle = pickPolylineStyleFromDataSource(st.targetDs) || st.turningLineStyle;

    showToast("End selected. Creating arc...", Type.NOTIFICATION.INFO, { duration: 1800 });
    applyArcNow();
  };

  st.handler.setInputAction((e) => {
    if (!e?.position) return;
    beginDrag(e.position);
  }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

  st.handler.setInputAction((e) => {
    if (!e?.endPosition) return;
    updateDrag(e.endPosition);
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  st.handler.setInputAction(() => {
    endDrag();
  }, Cesium.ScreenSpaceEventType.LEFT_UP);
};

// -----------------------
// Public API
// -----------------------
export function toggleTurningArcTool(
  viewer,
  onEdit,
  disable,
  curbstoneDs,
  roadedgeDs,
  guardrailDs,
  concreteBarrierDs,
  laneMarkerDs,
  virtualLineDs,
  turningLineDs,
  turningArcDs,
  originalCameraControls,
  onDisable,
  userData,
  mapObjectsRef = null,
  setMapObjects = null,
) {
  if (!viewer) return;

  if (disable) {
    if (!st.enabled) return;
    showToast("Turning Arc tool disabled.", Type.NOTIFICATION.INFO, { duration: 2000 });
    cleanup();
    return;
  }

  cleanup();

  st.enabled = true;
  st.viewer = viewer;
  st.onEdit = onEdit || (() => {});
  st.onDisable = typeof onDisable === "function" ? onDisable : null;
  st.userData = userData || null;
  st.mapObjectsRef = mapObjectsRef || null;
  st.setMapObjects = typeof setMapObjects === "function" ? setMapObjects : null;

  st.originalCameraControls = originalCameraControls || null;
  st.camLocked = false;

  st.curbstoneDs = curbstoneDs || null;
  st.roadedgeDs = roadedgeDs || null;
  st.guardrailDs = guardrailDs || null;
  st.concreteBarrierDs = concreteBarrierDs || null;

  st.laneMarkerDs = laneMarkerDs || null;
  st.virtualLineDs = virtualLineDs || null;
  st.turningLineDs = turningLineDs || null;
  st.turningArcDs = turningArcDs || null;

  const hasAny = Boolean(st.curbstoneDs || st.roadedgeDs || st.guardrailDs || st.concreteBarrierDs || st.laneMarkerDs || st.virtualLineDs || st.turningLineDs || st.turningArcDs);

  if (!hasAny) {
    showToast("Turning Arc tool needs at least one source layer (Curbstone/RoadEdge/Guardrail/ConcreteBarrier OR LaneMarker/VirtualLine/TurningLine/TurningArc).", Type.NOTIFICATION.ERROR, {
      duration: 6000,
    });

    cleanup();
    return;
  }

  st.turningLineStyle =
    pickPolylineStyleFromDataSource(st.curbstoneDs) ||
    pickPolylineStyleFromDataSource(st.roadedgeDs) ||
    pickPolylineStyleFromDataSource(st.guardrailDs) ||
    pickPolylineStyleFromDataSource(st.concreteBarrierDs) ||
    pickPolylineStyleFromDataSource(st.laneMarkerDs) ||
    pickPolylineStyleFromDataSource(st.virtualLineDs) ||
    pickPolylineStyleFromDataSource(st.turningLineDs) ||
    pickPolylineStyleFromDataSource(st.turningArcDs) ||
    null;

  rebuildEndpointCache();
  setupHandler();

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);

  showToast("Turning Arc tool enabled. Draw a box on ONE endpoint to set start, then draw another box for end.", Type.NOTIFICATION.SUCCESS, { duration: 5500 });
}

export function undoTurningArc() {
  const stack = st.undoStack;

  if (!Array.isArray(stack) || !stack.length) {
    showToast("Nothing to undo.", Type.NOTIFICATION.INFO, { duration: 2000 });
    return false;
  }

  let removed = false;
  let removedId = null;

  while (stack.length) {
    const last = stack.pop();
    const ds = last?.ds;
    const id = last?.id;

    if (!ds || !id) continue;

    try {
      const ents = ds.entities;
      const ent = ents?.getById?.(id) || ents?.values?.find?.((e) => e?.id === id) || null;
      if (ent && ents) {
        ents.remove(ent);
        removed = true;
        removedId = id;
        break;
      }
    } catch {}
  }

  if (!removed) {
    showToast("Nothing to undo.", Type.NOTIFICATION.INFO, { duration: 2000 });
    return false;
  }

  if (st.arcEntity && st.arcEntity.id === removedId) {
    try {
      st.arcEntity = null;
      st.arcNumericId = null;
      st.canAdjust = false;
      resetSelection(true);
    } catch {}
  }

  try {
    rebuildEndpointCache();
  } catch {}

  showToast("Undo: last turning arc removed.", Type.NOTIFICATION.SUCCESS, { duration: 2000 });
  return true;
}
